import * as plugins from './plugins.js';
import { TypedRouter } from './classes.typedrouter.js';

const closingBit: any = '#############CLOSING BIT#############';

export interface ICommFunctions {
  sendMethod?: (
    sendPayload: plugins.typedRequestInterfaces.IStreamRequest
  ) => Promise<plugins.typedRequestInterfaces.IStreamRequest>;
  typedrouter?: TypedRouter<any>;
  activationScope?: VirtualStreamActivationScope;
}

export class VirtualStreamClosedError extends Error {
  constructor(messageArg = 'VirtualStream is closed', optionsArg?: ErrorOptions) {
    super(messageArg, optionsArg);
    this.name = 'VirtualStreamClosedError';
  }
}

/**
 * Tracks only the streams activated during one request/response handshake.
 * Failed handshakes can therefore roll back their own resources without
 * closing a stream that was already active before the request started.
 */
export class VirtualStreamActivationScope {
  private trackedStreams = new Set<VirtualStream<any>>();
  private settled = false;

  public track(streamArg: VirtualStream<any>): void {
    if (!this.settled) {
      this.trackedStreams.add(streamArg);
    }
  }

  public commit(): void {
    const streams = [...this.trackedStreams];
    this.settled = true;
    this.trackedStreams.clear();
    for (const stream of streams) {
      stream.commitActivation();
    }
  }

  public async rollback(reasonArg?: unknown): Promise<void> {
    if (this.settled) {
      return;
    }
    this.settled = true;
    const streams = [...this.trackedStreams];
    this.trackedStreams.clear();
    await Promise.all(
      streams.map((streamArg) => streamArg.abort(reasonArg))
    );
  }
}

export type TVirtualStreamLifecycleState = 'idle' | 'active' | 'closed';

/**
 * 1. A VirtualStream connects over the network.
 * 2. It is always paired to one other VirtualStream on the other side with the same streamId.
 * 3. It has a readable and writable side.
 * 4. The writable side is readable on the other side and vice versa.
 */
export class VirtualStream<T = Uint8Array>
  implements plugins.typedRequestInterfaces.IVirtualStream<T>
{
  public static respondingRegistrationIdleTimeoutMs = 15_000;
  public static gracefulCloseTimeoutMs = 10_000;

  public static encodePayloadForNetwork(
    objectPayload: any,
    commFunctions: ICommFunctions,
    originalPayload?: any,
    path: string[] = []
  ): any {
    if (!objectPayload) {
      return objectPayload;
    }
    if (plugins.smartbuffer.isBufferLike(objectPayload)) {
      return objectPayload;
    }
    if (objectPayload instanceof VirtualStream) {
      objectPayload.activate(commFunctions);
      if (!originalPayload?.response || path.includes('response')) {
        return {
          _isVirtualStream: true,
          streamId: objectPayload.streamId,
        };
      }
      return {
        _OBMITTED_VIRTUAL_STREAM: true,
        reason: 'path is under .request: omitted for deduplication reasons in response cycle.',
      };
    }
    if (Array.isArray(objectPayload)) {
      return objectPayload.map((item, index) =>
        VirtualStream.encodePayloadForNetwork(
          item,
          commFunctions,
          originalPayload || objectPayload,
          path.concat(String(index))
        )
      );
    }
    if (objectPayload !== null && typeof objectPayload === 'object') {
      if (
        objectPayload instanceof Set ||
        objectPayload instanceof Map ||
        objectPayload instanceof Date ||
        objectPayload instanceof RegExp ||
        objectPayload instanceof Error ||
        objectPayload instanceof Promise ||
        typeof objectPayload.then === 'function'
      ) {
        return objectPayload;
      }
      return Object.entries(objectPayload).reduce<Record<string, any>>(
        (accumulatorArg, [key, value]) => {
          accumulatorArg[key] = VirtualStream.encodePayloadForNetwork(
            value,
            commFunctions,
            originalPayload || objectPayload,
            path.concat(key)
          );
          return accumulatorArg;
        },
        {}
      );
    }
    return objectPayload;
  }

  public static decodePayloadFromNetwork(
    objectPayload: any,
    commFunctions: ICommFunctions
  ): any {
    return VirtualStream.decodePayloadFromNetworkInternal(
      objectPayload,
      commFunctions,
      new Map<string, VirtualStream<any>>()
    );
  }

  public static containsNetworkDescriptor(objectPayload: any): boolean {
    if (
      !objectPayload ||
      plugins.smartbuffer.isBufferLike(objectPayload) ||
      typeof objectPayload !== 'object'
    ) {
      return false;
    }
    if (objectPayload._isVirtualStream === true) {
      return true;
    }
    if (Array.isArray(objectPayload)) {
      return objectPayload.some((itemArg) =>
        VirtualStream.containsNetworkDescriptor(itemArg)
      );
    }
    return Object.values(objectPayload).some((valueArg) =>
      VirtualStream.containsNetworkDescriptor(valueArg)
    );
  }

  private static decodePayloadFromNetworkInternal(
    objectPayload: any,
    commFunctions: ICommFunctions,
    decodedStreamsArg: Map<string, VirtualStream<any>>
  ): any {
    if (
      plugins.smartbuffer.isBufferLike(objectPayload) ||
      objectPayload instanceof TypedRouter
    ) {
      return objectPayload;
    }
    if (objectPayload === null || typeof objectPayload !== 'object') {
      return objectPayload;
    }
    if (
      objectPayload instanceof Set ||
      objectPayload instanceof Map ||
      objectPayload instanceof Date ||
      objectPayload instanceof RegExp ||
      objectPayload instanceof Error ||
      objectPayload instanceof Promise ||
      typeof objectPayload.then === 'function'
    ) {
      return objectPayload;
    }
    if (objectPayload._isVirtualStream === true) {
      if (
        typeof objectPayload.streamId !== 'string' ||
        objectPayload.streamId.length === 0
      ) {
        throw new TypeError('VirtualStream network descriptor requires a non-empty streamId');
      }
      const decodedStream = decodedStreamsArg.get(objectPayload.streamId);
      if (decodedStream) {
        return decodedStream;
      }
      const registeredStream =
        commFunctions.typedrouter?.getRegisteredVirtualStreamById(
          objectPayload.streamId
        );
      if (registeredStream) {
        registeredStream.refreshRespondingRegistration();
        decodedStreamsArg.set(objectPayload.streamId, registeredStream);
        return registeredStream;
      }
      const virtualStream = new VirtualStream();
      virtualStream.streamId = objectPayload.streamId;
      virtualStream.activate(commFunctions);
      decodedStreamsArg.set(objectPayload.streamId, virtualStream);
      return virtualStream;
    }
    if (Array.isArray(objectPayload)) {
      return objectPayload.map((itemArg) =>
        VirtualStream.decodePayloadFromNetworkInternal(
          itemArg,
          commFunctions,
          decodedStreamsArg
        )
      );
    }
    return Object.keys(objectPayload).reduce<Record<string, any>>(
      (accumulatorArg, keyArg) => {
        accumulatorArg[keyArg] =
          VirtualStream.decodePayloadFromNetworkInternal(
            objectPayload[keyArg],
            commFunctions,
            decodedStreamsArg
          );
        return accumulatorArg;
      },
      {}
    );
  }

  public side!: 'requesting' | 'responding';
  public streamId: string = plugins.isounique.uni();
  public sendMethod!: ICommFunctions['sendMethod'];
  public typedrouter!: TypedRouter<any>;

  private lifecycleStateInternal: TVirtualStreamLifecycleState = 'idle';
  private closeError: VirtualStreamClosedError | undefined;
  private keepAlive = true;
  private keepAliveLoopPromise: Promise<void> | undefined;
  private queueWorkPromise: Promise<void> | undefined;
  private sendAdmissionTail: Promise<void> = Promise.resolve();
  private frameAdmissionTail: Promise<void> = Promise.resolve();
  private closeStarted = false;
  private activationCommitted = false;
  private activationResolvers = new Set<() => void>();
  private respondingDrainResolvers = new Set<() => void>();
  private respondingExpiryTimer:
    | ReturnType<typeof setTimeout>
    | undefined;

  private sendBackpressuredArray = new plugins.lik.BackpressuredArray<T>(16);
  private receiveBackpressuredArray = new plugins.lik.BackpressuredArray<T>(16);

  public get lifecycleState(): TVirtualStreamLifecycleState {
    return this.lifecycleStateInternal;
  }

  private activate(commFunctionsArg: ICommFunctions): void {
    if (this.lifecycleStateInternal === 'closed') {
      throw this.getCloseError();
    }

    const requestedSide = commFunctionsArg.sendMethod
      ? 'requesting'
      : commFunctionsArg.typedrouter
        ? 'responding'
        : undefined;
    if (!requestedSide) {
      throw new Error('VirtualStream requires a send method or TypedRouter');
    }
    if (
      this.lifecycleStateInternal === 'active' &&
      this.side !== requestedSide
    ) {
      throw new Error(
        `VirtualStream ${this.streamId} is already active on the ${this.side} side`
      );
    }

    const newlyActivated = this.lifecycleStateInternal === 'idle';
    this.lifecycleStateInternal = 'active';
    this.keepAlive = true;
    this.side = requestedSide;
    if (requestedSide === 'requesting') {
      this.sendMethod = commFunctionsArg.sendMethod;
    } else {
      this.typedrouter = commFunctionsArg.typedrouter!;
      const registeredStream = this.typedrouter.registerVirtualStream(this);
      if (registeredStream !== this) {
        this.lifecycleStateInternal = 'idle';
        this.typedrouter = undefined!;
        throw new Error(
          `VirtualStream ${this.streamId} is already registered on this router`
        );
      }
      this.refreshRespondingRegistration();
    }
    if (newlyActivated) {
      if (commFunctionsArg.activationScope) {
        commFunctionsArg.activationScope.track(this);
      } else {
        this.commitActivation();
      }
    }
  }

  public commitActivation(): void {
    if (
      this.lifecycleStateInternal === 'active' &&
      this.side === 'requesting'
    ) {
      this.activationCommitted = true;
      const activationResolvers = [...this.activationResolvers];
      this.activationResolvers.clear();
      for (const resolve of activationResolvers) {
        resolve();
      }
      this.startKeepAliveLoop();
      this.ensureQueueWork();
    }
  }

  private getCloseError(reasonArg?: unknown): VirtualStreamClosedError {
    if (!this.closeError) {
      this.closeError = new VirtualStreamClosedError(
        'VirtualStream is closed',
        reasonArg === undefined ? undefined : { cause: reasonArg }
      );
    }
    return this.closeError;
  }

  private assertNotClosed(): void {
    if (this.lifecycleStateInternal === 'closed') {
      throw this.getCloseError();
    }
  }

  private assertCanSend(): void {
    this.assertNotClosed();
    if (this.closeStarted) {
      throw new VirtualStreamClosedError('VirtualStream is closing');
    }
  }

  private async acquireSendAdmission(): Promise<() => void> {
    const previousAdmission = this.sendAdmissionTail;
    let releaseAdmission!: () => void;
    this.sendAdmissionTail = new Promise<void>((resolve) => {
      releaseAdmission = resolve;
    });
    await previousAdmission;
    return releaseAdmission;
  }

  private async acquireFrameAdmission(): Promise<() => void> {
    const previousAdmission = this.frameAdmissionTail;
    let releaseAdmission!: () => void;
    this.frameAdmissionTail = new Promise<void>((resolve) => {
      releaseAdmission = resolve;
    });
    await previousAdmission;
    return releaseAdmission;
  }

  private async waitForOutboundDrain(): Promise<void> {
    while (
      this.sendBackpressuredArray.data.length > 0 ||
      this.queueWorkPromise
    ) {
      this.assertNotClosed();
      this.ensureQueueWork();
      const currentQueueWork = this.queueWorkPromise;
      if (currentQueueWork) {
        await currentQueueWork;
      } else if (!this.activationCommitted) {
        await new Promise<void>((resolve) => {
          this.activationResolvers.add(resolve);
        });
      }
    }
    this.assertNotClosed();
  }

  private async waitWithinGracefulCloseDeadline(
    promiseArg: Promise<void>
  ): Promise<void> {
    let timeout:
      | ReturnType<typeof setTimeout>
      | undefined;
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
      timeout = setTimeout(() => {
        reject(
          new VirtualStreamClosedError(
            'VirtualStream graceful close timed out'
          )
        );
      }, VirtualStream.gracefulCloseTimeoutMs);
      timeout.unref?.();
    });
    try {
      await Promise.race([promiseArg, timeoutPromise]);
    } finally {
      if (timeout) {
        clearTimeout(timeout);
      }
    }
  }

  private async waitForRespondingDrain(): Promise<void> {
    if (this.sendBackpressuredArray.data.length === 0) {
      return;
    }
    await new Promise<void>((resolve) => {
      this.respondingDrainResolvers.add(resolve);
    });
  }

  private resolveRespondingDrain(): void {
    if (this.sendBackpressuredArray.data.length > 0) {
      return;
    }
    const drainResolvers = [...this.respondingDrainResolvers];
    this.respondingDrainResolvers.clear();
    for (const resolve of drainResolvers) {
      resolve();
    }
  }

  private static reconstituteBinaryData(data: any): any {
    if (
      data == null ||
      data instanceof Uint8Array ||
      (typeof Buffer !== 'undefined' && Buffer.isBuffer(data))
    ) {
      return data;
    }
    if (typeof data === 'object') {
      if (
        data.type === 'EncodedBuffer' &&
        typeof data.data === 'string' &&
        data.data.startsWith('base64:')
      ) {
        const base64Data = data.data.slice('base64:'.length);
        if (typeof Buffer !== 'undefined') {
          const buffer = Buffer.from(base64Data, 'base64');
          return new Uint8Array(
            buffer.buffer,
            buffer.byteOffset,
            buffer.byteLength
          );
        }
        return new Uint8Array(
          Array.from(atob(base64Data)).map((characterArg) =>
            characterArg.charCodeAt(0)
          )
        );
      }
      if (data.type === 'Buffer' && Array.isArray(data.data)) {
        return new Uint8Array(data.data);
      }
      const keys = Object.keys(data);
      if (keys.length > 0 && keys.every((keyArg) => /^\d+$/.test(keyArg))) {
        const array = new Array(keys.length);
        for (const keyArg of keys) {
          array[Number(keyArg)] = data[keyArg];
        }
        if (array.every((valueArg) => typeof valueArg === 'number')) {
          return new Uint8Array(array);
        }
      }
    }
    return data;
  }

  private ensureQueueWork(): void {
    if (
      this.lifecycleStateInternal === 'closed' ||
      this.side !== 'requesting' ||
      !this.activationCommitted ||
      this.queueWorkPromise
    ) {
      return;
    }
    this.queueWorkPromise = this.workOnQueue()
      .catch(async (errorArg) => {
        await this.abort(errorArg);
      })
      .finally(() => {
        this.queueWorkPromise = undefined;
        if (
          this.lifecycleStateInternal === 'active' &&
          this.side === 'requesting' &&
          this.sendBackpressuredArray.data.length > 0
        ) {
          this.ensureQueueWork();
        }
      });
  }

  private async workOnQueue(): Promise<void> {
    this.assertNotClosed();
    let thisSideIsBackpressured =
      !this.receiveBackpressuredArray.checkSpaceAvailable();
    let otherSideHasNext = false;
    let otherSideIsBackpressured = false;

    const getFeedback = async (): Promise<void> => {
      const streamTr = await this.sendStreamRequest({
        streamId: this.streamId,
        cycleId: plugins.isounique.uni(),
        cycle: 'request',
        mainPurpose: 'feedback',
        next: this.sendBackpressuredArray.data.length > 0,
        backpressure: !this.receiveBackpressuredArray.checkSpaceAvailable(),
      });
      if (!this.acceptRemoteResponse(streamTr)) {
        return;
      }
      otherSideIsBackpressured = Boolean(
        streamTr.response?.backpressure
      );
      otherSideHasNext = Boolean(streamTr.response?.next);
    };

    await getFeedback();
    while (this.sendBackpressuredArray.data.length > 0 || otherSideHasNext) {
      this.assertNotClosed();
      if (
        otherSideHasNext &&
        this.sendBackpressuredArray.data.length === 0 &&
        !this.receiveBackpressuredArray.checkSpaceAvailable()
      ) {
        await this.receiveBackpressuredArray.waitForSpace();
        this.assertNotClosed();
        thisSideIsBackpressured =
          !this.receiveBackpressuredArray.checkSpaceAvailable();
      }
      while (otherSideIsBackpressured) {
        await plugins.smartdelay.delayFor(50);
        this.assertNotClosed();
        await getFeedback();
      }

      const dataArg =
        this.sendBackpressuredArray.data.length > 0
          ? this.sendBackpressuredArray.shift()
          : undefined;
      const streamTr = await this.sendStreamRequest({
        streamId: this.streamId,
        cycleId: plugins.isounique.uni(),
        cycle: 'request',
        mainPurpose: dataArg === undefined ? 'read' : 'chunk',
        backpressure: thisSideIsBackpressured,
        next: this.sendBackpressuredArray.data.length > 0,
        ...(dataArg === undefined ? {} : { chunkData: dataArg }),
      });
      if (!this.acceptRemoteResponse(streamTr)) {
        return;
      }
      otherSideIsBackpressured = Boolean(
        streamTr.response?.backpressure
      );
      thisSideIsBackpressured =
        !this.receiveBackpressuredArray.checkSpaceAvailable();
      otherSideHasNext = Boolean(streamTr.response?.next);
    }
  }

  private async sendStreamRequest(
    requestArg: plugins.typedRequestInterfaces.IUnifiedStreamDataPackage
  ): Promise<plugins.typedRequestInterfaces.IStreamRequest> {
    const releaseAdmission = await this.acquireFrameAdmission();
    try {
      this.assertNotClosed();
      if (!this.sendMethod) {
        throw new Error('VirtualStream is missing a send method');
      }
      return await this.sendMethod({
        method: '##VirtualStream##',
        request: requestArg,
        response: null as any,
      });
    } finally {
      releaseAdmission();
    }
  }

  private acceptRemoteResponse(
    streamTrArg: plugins.typedRequestInterfaces.IStreamRequest
  ): boolean {
    if (this.lifecycleStateInternal === 'closed') {
      return false;
    }
    const responseChunk = streamTrArg.response?.chunkData;
    if (responseChunk !== undefined) {
      this.receiveBackpressuredArray.push(
        VirtualStream.reconstituteBinaryData(responseChunk) as T
      );
    }
    if (
      streamTrArg.response?.keepAlive === false ||
      streamTrArg.response?.mainPurpose === 'end'
    ) {
      this.terminate(
        new VirtualStreamClosedError('Remote VirtualStream is closed'),
        responseChunk === closingBit
      );
      return false;
    }
    return true;
  }

  public async handleStreamTr(
    streamTrArg: plugins.typedRequestInterfaces.IStreamRequest
  ): Promise<plugins.typedRequestInterfaces.IStreamRequest> {
    if (this.lifecycleStateInternal === 'closed') {
      streamTrArg.response = this.createClosedResponse(streamTrArg);
      return streamTrArg;
    }
    this.refreshRespondingRegistration();
    if (
      streamTrArg.request.keepAlive === false ||
      streamTrArg.request.mainPurpose === 'end'
    ) {
      streamTrArg.response = this.createClosedResponse(streamTrArg);
      await this.abort();
      return streamTrArg;
    }
    const receivedClosingBit =
      streamTrArg.request.chunkData === closingBit;
    if (streamTrArg.request.chunkData !== undefined) {
      this.receiveBackpressuredArray.push(
        VirtualStream.reconstituteBinaryData(
          streamTrArg.request.chunkData
        ) as T
      );
    }

    const requesterIsBackpressured = Boolean(
      streamTrArg.request.backpressure
    );
    const responseChunk =
      !receivedClosingBit &&
      !requesterIsBackpressured &&
      this.sendBackpressuredArray.data.length > 0
        ? this.sendBackpressuredArray.shift()
        : undefined;
    this.resolveRespondingDrain();
    const sentClosingBit = responseChunk === closingBit;
    streamTrArg.response = {
      streamId: this.streamId,
      cycleId: streamTrArg.request.cycleId,
      cycle: 'response',
      mainPurpose:
        sentClosingBit
          ? 'end'
          : responseChunk !== undefined
          ? 'chunk'
          : streamTrArg.request.mainPurpose === 'keepAlive'
            ? 'keepAlive'
            : 'feedback',
      keepAlive: !sentClosingBit,
      next: this.sendBackpressuredArray.data.length > 0,
      backpressure: !this.receiveBackpressuredArray.checkSpaceAvailable(),
      ...(responseChunk === undefined ? {} : { chunkData: responseChunk }),
    };
    if (receivedClosingBit || sentClosingBit) {
      streamTrArg.response.mainPurpose = 'end';
      streamTrArg.response.keepAlive = false;
      this.terminate(undefined, receivedClosingBit);
    }
    return streamTrArg;
  }

  private createClosedResponse(
    streamTrArg: plugins.typedRequestInterfaces.IStreamRequest
  ): plugins.typedRequestInterfaces.IUnifiedStreamDataPackage {
    return {
      streamId: streamTrArg.request.streamId,
      cycleId: streamTrArg.request.cycleId,
      cycle: 'response',
      mainPurpose: 'end',
      keepAlive: false,
      next: false,
      backpressure: false,
    };
  }

  public refreshRespondingRegistration(): void {
    if (
      this.side !== 'responding' ||
      this.lifecycleStateInternal !== 'active'
    ) {
      return;
    }
    if (this.respondingExpiryTimer) {
      clearTimeout(this.respondingExpiryTimer);
    }
    this.respondingExpiryTimer = setTimeout(() => {
      void this.abort(
        new VirtualStreamClosedError(
          'Responding VirtualStream registration expired'
        )
      );
    }, VirtualStream.respondingRegistrationIdleTimeoutMs);
    this.respondingExpiryTimer.unref?.();
  }

  public async cleanup(): Promise<void> {
    await this.abort();
  }

  private cleanupRegistration(): void {
    if (this.respondingExpiryTimer) {
      clearTimeout(this.respondingExpiryTimer);
      this.respondingExpiryTimer = undefined;
    }
    if (this.typedrouter) {
      const typedrouter = this.typedrouter;
      this.typedrouter = undefined!;
      typedrouter.unregisterVirtualStream(this);
    }
  }

  private startKeepAliveLoop(): void {
    if (
      this.side !== 'requesting' ||
      this.lifecycleStateInternal === 'closed' ||
      this.keepAliveLoopPromise
    ) {
      return;
    }
    this.keepAliveLoopPromise = this.runKeepAliveLoop()
      .catch(async (errorArg) => {
        await this.abort(errorArg);
      })
      .finally(() => {
        this.keepAliveLoopPromise = undefined;
      });
  }

  private async runKeepAliveLoop(): Promise<void> {
    await plugins.smartdelay.delayFor(0);
    while (
      this.keepAlive &&
      this.lifecycleStateInternal === 'active'
    ) {
      await this.triggerKeepAlive();
      if (
        this.keepAlive &&
        this.lifecycleStateInternal === 'active'
      ) {
        await plugins.smartdelay.delayFor(1000);
      }
    }
  }

  private async triggerKeepAlive(): Promise<void> {
    const streamTr = await this.sendStreamRequest({
      streamId: this.streamId,
      cycleId: plugins.isounique.uni(),
      cycle: 'request',
      mainPurpose: 'keepAlive',
      keepAlive: true,
      backpressure:
        !this.receiveBackpressuredArray.checkSpaceAvailable(),
    });
    if (!this.acceptRemoteResponse(streamTr)) {
      return;
    }
    if (streamTr.response?.next) {
      this.ensureQueueWork();
    }
  }

  public async sendData(dataArg: T): Promise<void> {
    const releaseAdmission = await this.acquireSendAdmission();
    try {
      this.assertCanSend();
      await this.sendBackpressuredArray.waitForSpace();
      this.assertCanSend();
      this.sendBackpressuredArray.push(dataArg);
      this.ensureQueueWork();
      await this.sendBackpressuredArray.waitForSpace();
      this.assertCanSend();
    } finally {
      releaseAdmission();
    }
  }

  public async fetchData(): Promise<T> {
    if (this.receiveBackpressuredArray.checkHasItems()) {
      return this.receiveBackpressuredArray.shift() as T;
    }
    this.assertNotClosed();
    await this.receiveBackpressuredArray.waitForItems();
    if (!this.receiveBackpressuredArray.checkHasItems()) {
      throw this.getCloseError();
    }
    return this.receiveBackpressuredArray.shift() as T;
  }

  public async readFromWebstream(
    readableStreamArg: ReadableStream<T>,
    closeAfterReading = true
  ): Promise<void> {
    const reader = readableStreamArg.getReader();
    try {
      let streamIsDone = false;
      while (!streamIsDone) {
        const { value, done } = await reader.read();
        if (value !== undefined) {
          await this.sendData(value);
        }
        streamIsDone = done;
      }
      if (closeAfterReading) {
        await this.close(true);
      }
    } finally {
      reader.releaseLock();
    }
  }

  public async writeToWebstream(
    writableStreamArg: WritableStream<T>
  ): Promise<void> {
    const writer = writableStreamArg.getWriter();
    try {
      while (
        this.lifecycleStateInternal !== 'closed' ||
        this.receiveBackpressuredArray.checkHasItems()
      ) {
        const value = await this.fetchData();
        if (value === closingBit) {
          break;
        }
        await writer.write(value);
      }
      await writer.close();
    } finally {
      writer.releaseLock();
    }
  }

  public async close(sendClosingBitArg = false): Promise<void> {
    if (this.lifecycleStateInternal === 'closed') {
      return;
    }
    this.closeStarted = true;
    if (sendClosingBitArg) {
      if (this.side === 'requesting' && this.sendMethod) {
        try {
          await this.waitWithinGracefulCloseDeadline(
            this.waitForOutboundDrain()
          );
          await this.sendStreamRequest({
            streamId: this.streamId,
            cycleId: plugins.isounique.uni(),
            cycle: 'request',
            mainPurpose: 'chunk',
            chunkData: closingBit,
            next: false,
            backpressure: false,
          });
        } catch {
          // Local teardown remains authoritative when the peer already disappeared.
        }
      } else if (this.side === 'responding') {
        this.sendBackpressuredArray.push(closingBit);
        try {
          await this.waitWithinGracefulCloseDeadline(
            this.waitForRespondingDrain()
          );
        } catch {
          // Local teardown remains authoritative when the peer disappeared.
        }
      }
    }
    await this.abort();
  }

  public async abort(reasonArg?: unknown): Promise<void> {
    this.terminate(reasonArg);
  }

  private terminate(
    reasonArg?: unknown,
    preserveReceivedDataArg = false
  ): void {
    if (this.lifecycleStateInternal === 'closed') {
      return;
    }
    this.getCloseError(reasonArg);
    this.lifecycleStateInternal = 'closed';
    this.keepAlive = false;
    this.activationCommitted = false;
    this.sendMethod = undefined;
    this.sendBackpressuredArray.data.length = 0;
    if (!preserveReceivedDataArg) {
      this.receiveBackpressuredArray.data.length = 0;
    }
    this.sendBackpressuredArray.destroy();
    this.receiveBackpressuredArray.destroy();
    const activationResolvers = [...this.activationResolvers];
    this.activationResolvers.clear();
    for (const resolve of activationResolvers) {
      resolve();
    }
    const drainResolvers = [...this.respondingDrainResolvers];
    this.respondingDrainResolvers.clear();
    for (const resolve of drainResolvers) {
      resolve();
    }
    this.cleanupRegistration();
  }
}
